import Button from "@/components/common/Button";
import Reveal from "@/components/common/Reveal";
import CountUp from "@/components/common/CountUp";
import Tilt from "@/components/common/Tilt";
import CandlestickBackdrop from "@/components/visual/CandlestickBackdrop";
import { ArrowRight } from "@/components/common/icons";
import { ACCOUNT_TIERS, HERO_STATS, fmtUSD } from "@/lib/data";
import { env } from "@/lib/env";
import { useLocale } from "@/i18n/useLocale";
import { chromeFor } from "@/content/chrome";
import { landingHeroFor } from "@/content/landing";
import { formatPercent } from "@/lib/format";
import type { Locale } from "@/i18n/locales";
import { pathFor } from "@/routes/manifest";

// Sample tier featured in the hero preview ticket. ACCOUNT_TIERS[3]
// is the $100K · 2-Step row — the popular tier and the AccountSelector's
// default selection. Featuring the same tier in both surfaces makes
// the "Configure your own →" anchor land on a configurator that's
// already showing the user the same numbers they just saw in the hero,
// rather than a different default that looks like a bait-and-switch.
// Reading from ACCOUNT_TIERS keeps the hero numbers in sync with the
// configurator — if a tier ever changes, both surfaces shift together.
const SAMPLE = ACCOUNT_TIERS[3]!;
const SAMPLE_SIZE_LABEL = `$${SAMPLE.size >= 1000 ? `${SAMPLE.size / 1000}K` : SAMPLE.size}`;

interface SampleRow {
  k: string;
  display: string;
  countTo?: number;
  countFormat?: (n: number, locale: Locale) => string;
}

// Numeric rows opt into <CountUp> by carrying `countTo` + `countFormat`;
// non-numeric rows ("1:100", and the two worded values) render as static text.
// Built per render because the row labels are locale-dependent while the figures
// they carry are not — "1:100" is a ratio and is identical in every language.
function sampleRowsFor(t: ReturnType<typeof landingHeroFor>, locale: Locale): readonly SampleRow[] {
  return [
    { k: t.ticket.rows.oneTimeFee, display: fmtUSD(SAMPLE.fee2Step, locale), countTo: SAMPLE.fee2Step, countFormat: fmtUSD },
    { k: t.ticket.rows.profitTarget, display: fmtUSD(SAMPLE.target2Step, locale), countTo: SAMPLE.target2Step, countFormat: fmtUSD },
    { k: t.ticket.rows.profitSplit, display: formatPercent(SAMPLE.splitPct, locale), countTo: SAMPLE.splitPct, countFormat: formatPercent },
    { k: t.ticket.rows.leverage, display: "1:100" },
    { k: t.ticket.rows.timeLimit, display: t.ticket.values.noTimeLimit },
    { k: t.ticket.rows.payoutCycle, display: t.ticket.values.weekly },
  ];
}

export default function Hero(): JSX.Element {
  const locale = useLocale();
  const t = landingHeroFor(locale);
  const chrome = chromeFor(locale);
  const sampleRows = sampleRowsFor(t, locale);

  return (
    <section className="section-page relative pt-header pb-8 overflow-hidden">
      <div className="brand-glow" aria-hidden="true" />
      <CandlestickBackdrop opacity={0.10} style={{ top: 0, height: "100%" }} />

      <div className="container-page relative z-[2] w-full">
        <div className="grid gap-10 lg:gap-14 items-center hero-grid">
          <Reveal>
            <h1 className="text-hero font-display font-bold">
              {t.headline.line1}
              <br />
              {t.headline.line2}
              <br />
              <span className="text-brand-gradient">{t.headline.line3Highlight}</span>
            </h1>
            <p className="text-fg-muted text-[16px] md:text-[18px] max-w-[520px] mt-4">
              {t.lede}
            </p>
            <div className="flex gap-2 md:gap-3 mt-6">
              <Button
                size="md"
                variant="primary"
                orbit
                href={env.registerUrl}
                className="md:h-[52px] md:px-7 md:text-[15px]"
              >
                {chrome.cta.getFunded} <ArrowRight />
              </Button>
              <Button
                size="md"
                variant="ghost"
                href={pathFor("/how-it-works", locale)}
                className="md:h-[52px] md:px-7 md:text-[15px]"
              >
                {t.ctaSecondary}
              </Button>
            </div>
          </Reveal>

          {/*
           * Hero preview ticket — replaces the previous MonitorFrame
           * + platform-preview.png screenshot. Pricing reads directly
           * from ACCOUNT_TIERS so the hero stays in sync with the
           * AccountSelector configurator below.
           *
           * The "Configure your own" footer link is an in-page
           * anchor pointing at #configure, which is set on the
           * AccountSelector section. Native browser scroll handles
           * it — react-router-dom v6 does not intercept hash-only
           * URLs.
           */}
          <Reveal delay={120} className="hidden md:block">
            <Tilt max={4} scale={1.005}>
              <div className="glass-card glass-card-glow">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.16em] uppercase border-b border-border px-5 py-4">
                  <span className="text-fg-dim">{t.ticket.sampleEvaluation} · {SAMPLE_SIZE_LABEL} · {t.ticket.twoStep}</span>
                  <span className="text-brand-emphasis">{t.ticket.from} {fmtUSD(SAMPLE.fee2Step, locale)}</span>
                </div>

                <div className="px-6 pt-6 pb-3">
                  <div className="font-mono text-[9px] tracking-[0.22em] text-fg-dim uppercase">
                    {t.ticket.accountSize}
                  </div>
                  <div
                    className="font-display text-brand-gradient mt-1 leading-none font-bold tracking-tight"
                    style={{ fontSize: 56, letterSpacing: "-0.04em" }}
                  >
                    <CountUp to={SAMPLE.size} duration={1800} format={fmtUSD} />
                  </div>
                </div>

                {sampleRows.map((row) => (
                  <div
                    key={row.k}
                    className="flex items-center justify-between px-5 py-3 border-t border-border font-mono text-[12px]"
                  >
                    <span className="text-fg-muted tracking-[0.08em]">{row.k}</span>
                    <span className="text-fg font-bold">
                      {row.countTo !== undefined && row.countFormat ? (
                        <CountUp to={row.countTo} duration={1200} format={row.countFormat} />
                      ) : (
                        row.display
                      )}
                    </span>
                  </div>
                ))}

                <div
                  className="border-t border-border px-5 py-4 text-center"
                  style={{ background: "linear-gradient(180deg, rgb(var(--brand) / 0.08), transparent)" }}
                >
                  <a
                    href="#configure"
                    className="font-mono text-[12px] text-brand-emphasis tracking-[0.06em] font-semibold uppercase inline-flex items-center gap-1.5"
                  >
                    {t.ticket.configureYourOwn} <ArrowRight />
                  </a>
                </div>
              </div>
            </Tilt>
          </Reveal>
        </div>

        <Reveal delay={240}>
          <div className="mt-8 lg:mt-10 glass-card grid grid-cols-1 md:grid-cols-3 gap-px overflow-hidden" style={{ background: "rgb(var(--border))" }}>
            {HERO_STATS.map((s, i) => (
              <div key={s.key} className="bg-card px-4 py-4 md:px-6 md:py-5 relative overflow-hidden">
                <div className="stat-value text-[clamp(22px,2.2vw,32px)]">
                  {s.kind === "number" ? (
                    <CountUp to={s.value} duration={1400 + i * 120} format={s.format} />
                  ) : (
                    <span>{t.stats[s.key].display}</span>
                  )}
                </div>
                <div className="text-[12px] text-fg-muted tracking-wide">{t.stats[s.key].label}</div>
                <span
                  aria-hidden="true"
                  className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-brand animate-float-slow"
                  style={{ boxShadow: "0 0 8px rgb(var(--brand))" }}
                />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
